/**
 * CreditBreakerActions — Retry / Continue / Stop banner shown in the copilot
 * when the AI-credit circuit breaker trips (the LLM response was paused/stopped
 * because the user has no commercial credit and no OSS fallback).
 *  - Réessayer : re-send the same request (e.g. after the balance refreshes).
 *  - Acheter & continuer : go buy AI credits, then continue.
 *  - Arrêter : dismiss / stop the task.
 */

import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { RefreshCw, ShoppingCart, X, AlertTriangle } from 'lucide-react'

interface Props {
  onRetry: () => void
  onStop: () => void
  creditsRemaining?: number | null
  buyRoute?: string
}

export function CreditBreakerActions({ onRetry, onStop, creditsRemaining, buyRoute = '/account/credits' }: Props) {
  const navigate = useNavigate()
  return (
    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
      <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Tâche IA interrompue — crédits IA épuisés
          {creditsRemaining != null ? ` (${creditsRemaining} restants)` : ''}. Que voulez-vous faire ?
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-8" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Réessayer
        </Button>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700" onClick={() => navigate(buyRoute)}>
          <ShoppingCart className="h-3.5 w-3.5 mr-1.5" /> Acheter & continuer
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-muted-foreground" onClick={onStop}>
          <X className="h-3.5 w-3.5 mr-1.5" /> Arrêter
        </Button>
      </div>
    </div>
  )
}

/** Detects the orchestrator's credit-exhaustion marker in a response string. */
export function isCreditBreakerText(text: string): boolean {
  return /⏸️|⏹️|Tâche IA (mise en pause|arrêtée)|Achetez des crédits|Crédits IA épuisés|Crédits insuffisants/i.test(text)
}
